import { useEffect, useRef } from "react";
import { X } from "lucide-react";
import { useTranslation } from "react-i18next";

/**
 * Modal that renders a map with client marker and optional geocerca polygon.
 * @param {boolean} visible - Whether the modal is open
 * @param {Function} onClose - Callback when modal is closed
 * @param {Object} mapData - { colorCliente, latitudCliente, longitudCliente, coordenadasGeocerca, colorGeocerca }
 */
export default function ModalMapaSeguimiento({ visible = false, onClose, mapData = null }) {
  const { t } = useTranslation();
  const mapRef = useRef(null);
  const mapInstanceRef = useRef(null);
  const markerRef = useRef(null);
  const polygonRef = useRef(null);

  useEffect(() => {
    if (!visible || !mapData || !window.google?.maps || !mapRef.current) return;

    const latCliente = parseFloat(mapData.latitudCliente);
    const lngCliente = parseFloat(mapData.longitudCliente);
    const hasClient = !Number.isNaN(latCliente) && !Number.isNaN(lngCliente);

    const center = hasClient
      ? { lat: latCliente, lng: lngCliente }
      : { lat: -17.8, lng: -63.2 };

    const map = new window.google.maps.Map(mapRef.current, {
      center,
      zoom: 14,
      mapTypeControl: true,
      streetViewControl: false,
      fullscreenControl: true,
      zoomControl: true,
    });

    mapInstanceRef.current = map;
    const bounds = new window.google.maps.LatLngBounds();

    if (hasClient && mapData.colorCliente) {
      const marker = new window.google.maps.Marker({
        map,
        position: { lat: latCliente, lng: lngCliente },
        title: t("label_cliente") || "Cliente",
      });
      markerRef.current = marker;
      bounds.extend(new window.google.maps.LatLng(latCliente, lngCliente));
    }

    let coordenadas = mapData.coordenadasGeocerca;
    if (typeof coordenadas === "string") {
      try {
        coordenadas = JSON.parse(coordenadas);
      } catch (_) {
        coordenadas = null;
      }
    }

    const parseCoord = (coord) => {
      if (typeof coord !== "object" || coord == null) return null;
      const lat = parseFloat(coord.x ?? coord.lat ?? coord.latitud);
      const lng = parseFloat(coord.y ?? coord.lng ?? coord.longitud);
      if (Number.isNaN(lat) || Number.isNaN(lng)) return null;
      return new window.google.maps.LatLng(lat, lng);
    };

    if (coordenadas && Array.isArray(coordenadas) && coordenadas.length > 0 && mapData.colorGeocerca) {
      let path = [];
      if (Array.isArray(coordenadas[0])) {
        path = (coordenadas[0] || []).map(parseCoord).filter(Boolean);
      } else {
        path = coordenadas.map(parseCoord).filter(Boolean);
      }
      if (path.length > 0) {
        const polygon = new window.google.maps.Polygon({
          paths: path,
          strokeColor: mapData.colorGeocerca,
          strokeOpacity: 0.8,
          strokeWeight: 2,
          fillColor: mapData.colorGeocerca,
          fillOpacity: 0.35,
          map,
        });
        polygonRef.current = polygon;
        path.forEach((p) => bounds.extend(p));
      }
    }

    if (bounds.getNorthEast().lat() !== bounds.getSouthWest().lat() || bounds.getNorthEast().lng() !== bounds.getSouthWest().lng()) {
      map.fitBounds(bounds);
    }
  }, [visible, mapData, t]);

  useEffect(() => {
    if (!visible) {
      markerRef.current = null;
      polygonRef.current = null;
      mapInstanceRef.current = null;
    }
  }, [visible]);

  if (!visible) return null;

  return (
    <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/40 p-4">
      <div className="bg-white rounded-lg shadow-xl border border-gray-200 w-full max-w-4xl h-[80vh] flex flex-col">
        <div className="flex items-center justify-between px-4 py-3 border-b border-gray-200 flex-shrink-0">
          <h3 className="text-lg font-semibold text-gray-900">
            {t("label_mapa") || "Mapa"}
          </h3>
          <button
            type="button"
            className="p-1 hover:bg-gray-100 rounded-lg transition-colors"
            onClick={onClose}
            aria-label={t("label_cerrar") || "Cerrar"}
          >
            <X className="h-5 w-5 text-gray-600" />
          </button>
        </div>
        <div className="flex-1 min-h-0 p-2">
          <div ref={mapRef} className="w-full h-full rounded border border-gray-200" style={{ minHeight: "320px" }} />
        </div>
        <div className="px-4 py-3 border-t border-gray-200 flex justify-end flex-shrink-0">
          <button
            type="button"
            className="px-4 py-2 bg-blue-500 hover:bg-blue-600 text-white rounded-md text-sm"
            onClick={onClose}
          >
            {t("label_cerrar") || "Cerrar"}
          </button>
        </div>
      </div>
    </div>
  );
}
